import type { Caractere, PalavraCanonica } from '../tipos';
import {
  HIFEN,
  TAMANHO_MAX_JOGADOR,
  TAMANHO_MIN_JOGADOR,
  ehCaractereAceito,
  ehHifen,
  mesmaLetra,
} from './alfabeto';
import type { IndiceLexico } from './indice';
import { caracteres, paraCanonica } from './normalizacao';

/**
 * Validação da palavra do jogador (§4.1).
 *
 * Duas etapas separadas de propósito:
 *
 *  - `filtrarEntrada` decide o que **entra no draft** (T01-05, T01-06).
 *    Caractere recusado nunca aparece na tela.
 *  - `validarPalavraBase` decide se o draft **é uma palavra jogável**.
 *    Um prefixo incompleto é inválido, mas nunca é erro (T02-06).
 */

export type MotivoInvalidez =
  | 'vazia'
  | 'muito-longa'
  | 'hifen-na-borda'
  | 'fora-do-dicionario'
  | 'sem-ancora'
  /** R5-15: a palavra existe, mas já está no tabuleiro. */
  | 'ja-no-tabuleiro';

export type ResultadoValidacao =
  | { readonly valido: true; readonly canonica: PalavraCanonica }
  | { readonly valido: false; readonly motivo: MotivoInvalidez };

export interface EntradaFiltrada {
  /** Texto aceito, em NFC, na caixa em que foi digitado. */
  readonly texto: string;
  /** Caracteres recusados, para o anúncio acessível de T01-06. */
  readonly recusados: readonly string[];
  /** `true` quando algo foi cortado por exceder 12 caracteres (T01-05). */
  readonly truncado: boolean;
}

/**
 * Filtra a entrada bruta do campo. Aceita letras do português e hífen interno;
 * recusa espaço, número, emoji e pontuação (§4.1).
 */
export function filtrarEntrada(
  bruto: string,
  limite: number = TAMANHO_MAX_JOGADOR,
): EntradaFiltrada {
  const recusados: string[] = [];
  const aceitos: string[] = [];
  let truncado = false;

  for (const caractere of caracteres(bruto)) {
    if (!ehCaractereAceito(caractere)) {
      recusados.push(caractere);
      continue;
    }
    if (aceitos.length >= limite) {
      truncado = true;
      continue;
    }
    aceitos.push(caractere);
  }

  return { texto: aceitos.join(''), recusados, truncado };
}

/** O hífen conta no limite, mas não pode abrir nem fechar a palavra (§4.1). */
export function hifenNaBorda(texto: string): boolean {
  const letras = caracteres(texto);
  const primeira = letras[0];
  const ultima = letras[letras.length - 1];
  return primeira === HIFEN || ultima === HIFEN;
}

/** Índices das letras do draft que podem receber sugestão vertical (§5.2, §5.3). */
export function indicesJogaveis(texto: string): readonly number[] {
  const letras = caracteres(texto);
  const saida: number[] = [];
  for (let i = 0; i < letras.length; i += 1) {
    const letra = letras[i];
    if (letra !== undefined && !ehHifen(letra)) saida.push(i);
  }
  return saida;
}

/** Todas as posições em que `ancora` aparece no draft (§4.1, T05-05). */
export function ocorrenciasDaAncora(texto: string, ancora: Caractere): readonly number[] {
  const letras = caracteres(paraCanonica(texto));
  const saida: number[] = [];
  for (let i = 0; i < letras.length; i += 1) {
    const letra = letras[i];
    if (letra !== undefined && mesmaLetra(letra, ancora)) saida.push(i);
  }
  return saida;
}

/**
 * Valida a palavra-base. Em rodada ancorada, `ancora` é obrigatória e a palavra
 * precisa conter exatamente aquela letra — `V` não é satisfeito por `W`, e
 * acentuadas só são satisfeitas pelo mesmo diacrítico (T05-06).
 *
 * `usadas` traz o que já está no tabuleiro (R5-15). É o último teste da lista,
 * e de propósito: dizer "essa palavra já está no tabuleiro" a quem escreveu
 * algo que nem palavra é seria responder à pergunta errada.
 */
export function validarPalavraBase(
  bruto: string,
  indice: IndiceLexico,
  ancora?: Caractere,
  usadas?: ReadonlySet<PalavraCanonica>,
): ResultadoValidacao {
  const canonica = paraCanonica(bruto);
  const tamanho = caracteres(canonica).length;

  if (tamanho < TAMANHO_MIN_JOGADOR) return { valido: false, motivo: 'vazia' };
  if (tamanho > TAMANHO_MAX_JOGADOR) return { valido: false, motivo: 'muito-longa' };
  if (hifenNaBorda(canonica)) return { valido: false, motivo: 'hifen-na-borda' };

  if (ancora !== undefined && ocorrenciasDaAncora(canonica, ancora).length === 0) {
    return { valido: false, motivo: 'sem-ancora' };
  }

  if (!indice.existe(canonica)) return { valido: false, motivo: 'fora-do-dicionario' };
  if (usadas?.has(canonica) === true) return { valido: false, motivo: 'ja-no-tabuleiro' };

  return { valido: true, canonica };
}
